import { usePageTitle } from "../../../../shared/hooks/usePageTitle";
import { useFocusOnMount } from "../../../../shared/hooks/useFocusOnMount";
import { useEffect } from "react";
import { appCheckFailure, appCheckFailureBody, appCheckSucceededOnPage, autoReloadAfterFirstRefusal, retryApiRead, type AutoReloadPage } from "../../../../shared/lib/appCheckRecovery";
import { PageActions } from "../../../../shared/ui/PageActions";
import type { MessageKey } from "../i18n";
import { useTranslation } from "../i18n/useTranslation";
import { PageHead } from "./PageShell";

/**
 * THE ONE "WE COULDN'T LOAD THIS" ON book.* (c3). An API read failed and the
 * person may try again.
 *
 * - The words are the screen's own approved keys (`title`, `body`, `retry`),
 *   except that when App Check is why the read failed the body says which
 *   (P-15: refused or unreachable, shared/lib/appCheckRecovery.ts).
 * - Try again is ALWAYS `retryApiRead(onRetry)`: `onRetry` refetches in
 *   place, unless the page's last App Check attempt produced no token, when it
 *   reloads instead (the SDK's one-day block after a 403 lives in page
 *   memory). Pass the plain refetch; never wrap it again.
 *
 * Two placements, one component:
 *
 * `page` (default): the whole page failed. Drawn inside a `PageShell`: its
 *   PageHead with the title, focus moved to that heading on arrival
 *   (useFocusOnMount) and exactly one live region (the body line, or the
 *   head itself when the approved body is empty, W-2); Try again goes to the
 *   shell's pinned action bar through PageActions. The document title is the
 *   visible heading.
 *
 * `notice`: one part of a page failed (the time step's slots). An inline
 *   danger notice, role="alert", with its Try again inside it. No focus move
 *   and no title: the page around it is still the page.
 *
 * `autoReload` (rc.15, the page variant only): the NAME of a page whose first
 * public read this is ("profile", "book", "appointment"). When that read was
 * refused by the App Check exchange, the page reloads once on its own
 * (autoReloadAfterFirstRefusal decides, and caps it). A name, never a path:
 * the appointment page's path carries a bearer token.
 *
 * DIAGNOSTICS (rc.15, privacy-safe): `data-appcheck` says why App Check
 * failed (refused | unreachable | network) when it did, and
 * `data-appcheck-stage` whether this page had a token before (first-read |
 * later). Never the token, the SDK's text, or anything from the request.
 */
export function LoadFailed({
  title,
  body,
  retry,
  onRetry,
  testId,
  variant = "page",
  autoReload,
}: {
  title: MessageKey;
  body: MessageKey;
  retry: MessageKey;
  /** The plain in-page refetch. LoadFailed wraps it in retryApiRead. */
  onRetry: () => void;
  testId: string;
  variant?: "page" | "notice";
  /** The page's name, for the one automatic reload of a refused first read (page variant). */
  autoReload?: AutoReloadPage;
}) {
  const props = { title, body, retry, onRetry, testId };
  return variant === "notice" ? <FailedNotice {...props} /> : <FailedPage {...props} autoReload={autoReload} />;
}

/** `data-appcheck` and `data-appcheck-stage`, or nothing when App Check did not fail. */
function appCheckDiagnostics(): Record<string, string> {
  const failure = appCheckFailure();
  return failure === null ? {} : { "data-appcheck": failure, "data-appcheck-stage": appCheckSucceededOnPage() ? "later" : "first-read" };
}

type Props = { title: MessageKey; body: MessageKey; retry: MessageKey; onRetry: () => void; testId: string };

function RetryButton({ retry, onRetry }: Pick<Props, "retry" | "onRetry">) {
  const { t } = useTranslation();
  return (
    <button type="button" className="b-btn v-primary is-full" onClick={() => retryApiRead(onRetry)}>
      {t(retry)}
    </button>
  );
}

function FailedPage({ title, body, retry, onRetry, testId, autoReload }: Props & { autoReload?: AutoReloadPage }) {
  const { t } = useTranslation();
  usePageTitle(t(title));
  const heading = useFocusOnMount();
  useEffect(() => {
    if (autoReload !== undefined) autoReloadAfterFirstRefusal(autoReload);
  }, [autoReload]);
  const sub = t(appCheckFailureBody() ?? body);
  return (
    <div data-testid={testId} {...appCheckDiagnostics()}>
      <PageHead title={t(title)} sub={sub || undefined} headingRef={heading} subRole="alert" />
      <PageActions className="b2-foot">
        <RetryButton retry={retry} onRetry={onRetry} />
      </PageActions>
    </div>
  );
}

function FailedNotice({ title, body, retry, onRetry, testId }: Props) {
  const { t } = useTranslation();
  const sub = t(appCheckFailureBody() ?? body);
  return (
    <div className="b-notice t-danger" role="alert" data-testid={testId} {...appCheckDiagnostics()}>
      <div className="b-notice-b">
        <b>{t(title)}</b>
        {sub ? <p className="b-p">{sub}</p> : null}
        <RetryButton retry={retry} onRetry={onRetry} />
      </div>
    </div>
  );
}
